/** English strings for the action-plans slice (namespace: actionPlans.*). */
export const actionPlansEn = {
  title: "Action plans",
  subtitle: "Create, structure and track your action plans.",
  loading: "Loading…",

  list: {
    newPlan: "New plan",
    creating: "Creating…",
    total: "Total",
    assessed: "assessed",
    empty: "No action plans yet.",
    emptyHint: "Create your first plan to start selecting actions.",
    open: "Open",
    delete: "Delete",
    confirmDelete: "Permanently delete this action plan?",
    untitled: "Untitled plan",
    nameLabel: "Plan name",
    namePlaceholder: "e.g. 2026 Action plan",
    startYear: "Start year",
    endYear: "End year",
    selectedCount: "{{count}} actions selected",
    created: "Created {{date}}",
    updated: "Updated {{date}}",
    inProgressCount: "{{count}} in progress",
    doneCount: "{{count}} done",
    notDoneCount: "{{count}} not done",
    draft: "Draft",
  },

  steps: {
    issues: "Choose my issues",
    actions: "Courses of action",
    prioritization: "Prioritization",
    plan: "Plan actions",
    track: "Track progress",
  },
  settings: {
    edit: "Edit name and period",
    title: "Plan name and period",
  },
  tour: {
    replay: "Replay the tutorial",
    next: "Next",
    prev: "Previous",
    done: "Got it!",
    progress: "{{current}} of {{total}}",
    actions: {
      progressTitle: "Progress bar",
      progressDesc:
        "You are on step 2 of 5 of the action plan. This bar shows your progress.",
      consultTitle: "Consult stakeholders",
      consultDesc:
        "This button lets you share your plan with the stakeholder categories to gather their input.",
      issuesTitle: "Themes and issues",
      issuesDesc:
        "Your courses of action are organized by theme, then by issue and objective.",
      selectTitle: "Selecting courses of action",
      selectDesc:
        "Tick the courses of action you want to include in your action plan.",
      commentTitle: "Comments",
      commentDesc:
        "Click this icon to view or add a comment on a course of action.",
      groupsTitle: "Consultation category",
      groupsDesc:
        "Assign one or more stakeholder categories to consult for this course of action.",
      addGoalTitle: "Add an objective",
      addGoalDesc:
        "Add a custom objective and its courses of action for this issue.",
      goalGroupsTitle: "Groups for the whole objective",
      goalGroupsDesc:
        "Assign stakeholder categories to every retained course of action under this objective at once.",
      rewordGoalTitle: "Edit an objective",
      rewordGoalDesc:
        "Edit the objective's name and the wording of its courses of action, in one place.",
      nextTitle: "Navigation",
      nextDesc:
        "When you're done, and once the stakeholders have answered the prioritization survey, you can move to step 3 “Prioritization”.",
    },
    prioritization: {
      progressTitle: "Progress bar",
      progressDesc:
        "You are on step 3 of 5 of the action plan. This bar shows your progress.",
      consultationTitle: "Consultation results",
      consultationDesc:
        "This table summarizes your stakeholders' responses.",
      filtersTitle: "Filters and search",
      filtersDesc:
        "Use these filters to show a subset of courses of action by theme, issue, score or group.",
      autoSortTitle: "Automatic sort",
      autoSortDesc:
        "This button automatically reorders the courses of action by descending score.",
      tableTitle: "Prioritization table",
      tableDesc:
        "Your courses of action sorted by priority. Click the number to enter a position.",
      prePrioTitle: "Working-group prioritization",
      prePrioDesc:
        "Assign a score from 1 (low priority) to 5 (high priority) to reflect the prioritization decided by your working group. It adds to the stakeholder scores to compute the total score.",
      commentsTitle: "Comments",
      commentsDesc:
        "Click the bubble to view the comments stakeholders left on this course of action.",
      nextTitle: "Navigation",
      nextDesc: "When you're done, move to the next step or go back.",
    },
  },
  plan: {
    heading: "Plan actions",
    help: "Enrich each course of action, where applicable, with a start and deadline, an owner, a target and an estimated cost.",
    noSelected: "Select courses of action first, in the previous steps.",
  },

  nav: {
    stepIndex: "Plan issues and objectives",
    backToList: "Back to list",
    previousStep: "Previous step: {{step}}",
    nextStep: "Next step: {{step}}",
  },

  issues: {
    heading: "Issue selection",
    help: "Choose the issues to work on. Including an issue selects all of its actions; you can refine them in the next step.",
    included: "Included",
    included_count: "{{selected}} / {{total}} actions",
    empty: "This plan has no issue structure yet.",
    emptyHint: "Structure comes from the self-assessment. Complete the questionnaire to populate this plan.",
    selectAll: "Include all",
    clearAll: "Clear all",
    selectShort: "all",
    clearShort: "none",
    selectThematicAria: "Include every issue in the {{thematic}} thematic",
    clearThematicAria: "Remove every issue in the {{thematic}} thematic",
    includedOf: "{{included}} / {{total}} included",
    colIssue: "Issue",
    colCompleted: "Completed",
    colGoals: "Obj.",
    colActions: "Courses",
    completedActions_one: "{{count}} completed action",
    completedActions_other: "{{count}} completed actions",
    goalsCount_one: "{{count}} objective",
    goalsCount_other: "{{count}} objectives",
    availableActions_one: "{{count}} course of action available",
    availableActions_other: "{{count}} courses of action available",
    resources: {
      experts_one: "{{count}} expert",
      experts_other: "{{count}} experts",
      tools_one: "{{count}} tool",
      tools_other: "{{count}} tools",
      aids_one: "{{count}} aid",
      aids_other: "{{count}} aids",
    },
    summaryTitle: "This plan",
    summaryIssues: "of {{total}} issues",
    summaryActions: "of {{total}} courses of action retained",
    summaryGoals: "objectives covered",
  },

  actions: {
    heading: "Courses of action & stakeholder consultation",
    help: "Tick the courses of action to include, reword them as needed, add a comment, and choose which stakeholder groups to consult. Then open a consultation below.",
    rewordAction: "Reword this action",
    rewordActionLabel: "Course-of-action wording",
    rewordGoal: "Reword this objective",
    rewordGoalLabel: "Objective name",
    rewordHelp:
      "If needed, refine the wording to better reflect the reality of your processes. Leave it blank to keep the default wording.",
    noActions: "No actions for this goal.",
    extraBadge: "Bonus",
    addAction: "Add a course of action",
    addGoal: "Add an objective",
    addGoalHelp:
      "Name the new objective and add at least a first course of action: an objective cannot exist without one.",
    addGoalActionLabel: "First course of action",
    goalNeedsAction: "An objective needs at least one action.",
    saving: "Saving…",
    deleteAction: "Delete this course of action",
    deleteGoal: "Delete this objective",
    confirmDeleteAction: "Delete this custom course of action?",
    confirmDeleteGoal: "Delete this custom objective and its courses of action?",
    addComment: "Add a comment",
    viewComment: "View comment",
    comment: "Comment",
    deleteComment: "Delete",
    commentPlaceholder: "Optional note to track this action's progress…",
    save: "Save",
    cancel: "Cancel",
    selectGroups: "Select groups",
    groupsCount_one: "{{count}} group",
    groupsCount_other: "{{count}} groups",
    noGroupsYet: "Create stakeholder groups below to assign them.",
    goalBadge: "Objective",
    noIssues: "No issue retained for this plan.",
    noIssuesHint:
      "Go back to “Choose my issues” to pick the issues to work on; their courses of action will appear here.",
    extraGoalBadge: "Added objective",
    editObjective: "Edit the objective and its courses of action",
    editObjectiveHelp:
      "Reword the objective and its courses of action. Clear a field to fall back to the original wording.",
    editObjectiveActions: "Courses of action under this objective",
    goalGroupsTitle: "Stakeholder groups (objective)",
    goalGroupsAssign: "Assign to the objective",
    goalGroupsMixed: "Mixed",
    goalGroupsNoSelection: "Nothing retained",
    cascadeTitle: "Replace the groups already chosen?",
    cascadeBody:
      "Some retained courses of action already have stakeholder groups of their own. Applying the objective's choice replaces them on all {{count}} retained courses of action.",
    cascadeConfirm: "Replace",
    goalsMissing_one: "{{count}} objective with nothing retained",
    goalsMissing_other: "{{count}} objectives with nothing retained",
    retainedOf: "{{selected}}/{{total}} retained",
    retainedSummary:
      "{{selected}} of {{total}} courses of action retained · {{assigned}} with stakeholder groups",
  },

  prioritization: {
    heading: "Prioritization",
    help: "See the stakeholder-consultation results and prioritize your courses of action. This step is optional.",
    order: "Priority order",
    score: "Score",
    workingGroupPriority: "Working-group priority",
    consultationGroups: "Consulted groups",
    consultationResults: "Consultation results",
    anonymised:
      "Individual responses are anonymised — only the aggregate score and unnamed comments are shown.",
    group: "Category",
    status: "Status",
    responses: "Responses",
    manage: "Actions",
    categoriesCount: "{{count}} categor(y/ies)",
    actionColumn: "Course of action",
    open: "Open",
    closed: "Closed",
    openConsultation: "Open consultation",
    closeConsultation: "Close consultation",
    clearResponses: "Clear responses",
    confirmClear:
      "Delete all responses for this group? The group itself is kept. This action is irreversible.",
    refreshScores: "Refresh scores",
    refreshed: "Scores refreshed.",
    search: "Search…",
    autoSort: "Auto-sort by score",
    reword: "Reword",
    addToPlan: "Add to the action plan",
    confirmRemoveActionTitle: "Delete from the action plan",
    confirmRemoveAction:
      "Delete this course of action from the plan? You can add it back while you stay on this page, or from the previous step once you leave.",
    removeOutOfFilter: "Delete out-of-filter actions",
    bulkRemoveTitle: "Delete out-of-filter actions",
    bulkRemoveIntro:
      "These courses of action are hidden by the current filter. Uncheck any you want to keep, then confirm the deletion. Deleted courses of action can be added back in the previous step.",
    bulkRemoveConfirm: "Delete {{count}} action(s)",
    bulkRemoveButton: "Delete out-of-filter actions ({{count}})",
    confirmRemove: "Delete from the plan every action hidden by the current filter?",
    filters: "Filters",
    filterThematic: "Theme",
    filterIssue: "Issue",
    filterGoal: "Goal",
    filterAllF: "All",
    filterAllM: "All",
    scoreMin: "Min. score",
    scoreMax: "Max. score",
    consulted: "Consulted",
    consultedYes: "Yes",
    consultedNo: "No",
    stakeholderCategory: "Stakeholder category",
    resetFilters: "Reset filters",
    tools: "Tools",
    removeFromPlan: "Delete from the plan",
    stakeholderComments: "Stakeholder comments",
    ownComment: "Your comment",
    stakeholderComments_one: "1 stakeholder comment",
    stakeholderComments_other: "{{count}} stakeholder comments",
    notRated: "Not yet rated",
    noSelected: "First select courses of action in the “Courses of action” step.",
    noGroups:
      "No consultation groups yet — assign stakeholder groups to your actions in the previous step.",
  },

  visualization: {
    heading: "Track my progress",
    help: "Track each action's progress. Changes are saved automatically.",
    status: "Status",
    responsible: "Owner",
    responsiblePlaceholder: "Owner name",
    startDate: "Start date",
    deadline: "Deadline",
    estimatedCost: "Estimated cost ($)",
    estimatedCostHours: "Estimated cost (hours)",
    target: "Target",
    targetPlaceholder: "Intended outcome",
    comment: "Comment",
    commentPlaceholder: "Notes and follow-up…",
    noSelected: "No selected actions to track.",
    downloadPdf: "Download PDF",
    downloadExcel: "Download Excel",
    print: "Print",
    history: "History",
    historyTitle: "Change history",
    exporting: "Exporting…",
    exportError: "The export could not be generated.",
    experts: "Experts",
    findAllExperts: "Consult all experts for this course of action",
    findFavoriteExperts_one: "Favorite expert ({{count}})",
    findFavoriteExperts_other: "Favorite experts ({{count}})",
    consultFavoriteExperts: "Consult your favorite experts for this course of action",
    noFavoriteExperts: "No favorite expert for this course of action",
    resources: "Resources",
  },

  status: {
    not_done: "Not done",
    in_progress: "In progress",
    done: "Done",
  },

  groups: {
    heading: "Stakeholder groups",
    help: "Manage the groups you'll consult about your actions.",
    name: "Group name",
    namePlaceholder: "e.g. Employees, Customers…",
    introMessage: "Intro message",
    add: "Add group",
    adding: "Adding…",
    delete: "Delete",
    empty: "No stakeholder category created yet.",
    optional: "Optional step",
    stepHelp:
      "Create categories (e.g. employees, management committee) so you can consult them in the next step on the courses of action you choose. Respondents rate each one from 1 to 5 and can leave comments.",
    rename: "Rename the {{name}} category",
    deleteNamed: "Delete the {{name}} category",
    confirmDelete: "Delete the “{{name}}” category?",
  },

  consultations: {
    consultButton: "Consult stakeholders",
    modalTitle: "Stakeholder consultation",
    modalHelp:
      "Each category with at least one assigned course of action gets a survey link (1–5 rating). Share the link or send the invitation by email.",
    noGroups: "No stakeholder categories.",
    newCategory: "New category",
    newCategoryPlaceholder: "New category name…",
    addCategory: "Add",
    assignToEnable:
      "Assign courses of action to this category to enable its survey.",
    timeEstimate: "{{count}} actions · ~{{minutes}} min",
    responses: "{{count}} responses",
    open: "Open",
    closed: "Closed",
    close: "Close",
    reopen: "Reopen",
    rename: "Rename",
    cancel: "Cancel",
    save: "Save",
    copyLink: "Copy link",
    linkCopied: "Link copied.",
    linkCopyFailed: "Could not copy the link.",
    email: "Invitation email",
    emailLoading: "Loading template…",
    emailBody: "Message",
    recipients_label: "Recipients",
    recipientsPlaceholder: "email1@example.com, email2@example.com",
    emailSave: "Save",
    emailSend: "Send",
    emailSaved: "Template saved.",
    emailSent: "Email sent to {{count}} recipient(s).",
  },

  errors: {
    loadFailed: "Failed to load.",
    retry: "Retry",
    saveFailed: "Failed to save.",
  },
  richText: {
    bold: "Bold",
    italic: "Italic",
    bulletList: "Bulleted list",
    numberedList: "Numbered list",
    link: "Link",
    linkPrompt: "URL",
  },
} as const;
